import { ArrowBack as BackButtonIcon } from "@mui/icons-material";
import React from "react";

import Button from "./Button";
import injectStores from "../RootStore/injectStores";
import type { RouterStore } from "stores/RouterStore";

interface BackButtonProps {
  routerStore?: RouterStore;
}

/**
 * `BackButton` is a React functional component that renders a button with a back icon.
 * It uses the `injectStores` higher-order component to inject the `routerStore` into its props.
 * The button triggers the `goBack` function from the `routerStore` when clicked.
 *
 * @component
 * @param {BackButtonProps} props - The props for the BackButton component.
 * @param {object} props.routerStore - The router store injected by `injectStores`.
 * @param {function} props.routerStore.goBack - The function to navigate back in the router history.
 *
 * @returns {JSX.Element} A button component with a back icon.
 */
const BackButton = injectStores(
  ({ routerStore }: BackButtonProps) => {
    const handleBack = () => {
      try {
        if (routerStore?.goBack) {
          routerStore.goBack();
        } else {
          console.warn(
            "RouterStore or goBack method not available, falling back to browser back"
          );
          if (window.history.length > 1) {
            window.history.back();
          }
        }
      } catch (error) {
        console.error("Error in BackButton:", error);
        // Fallback to browser back
        if (window.history.length > 1) {
          window.history.back();
        }
      }
    };

    return (
      <Button
        tooltip="Go Back"
        color="white"
        justIcon
        autosize
        onClick={handleBack}
      >
        <BackButtonIcon />
      </Button>
    );
  }
);

export default BackButton;
